import React from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Star, ArrowUpRight, Heart } from "lucide-react";
import { useCart } from "../lib/cart";
import { useWishlist } from "../lib/wishlist";
import { inr } from "../lib/api";

export default function ProductCard({ product, index = 0 }) {
  const { addItem } = useCart();
  const { has, toggle } = useWishlist();
  const wished = has(product.id);
  const hasDiscount = product.compare_at_price && product.compare_at_price > product.price;

  return (
    <article
      data-testid={`product-card-${product.slug}`}
      className="group welljio-premium-card overflow-hidden flex flex-col relative"
      style={{ animationDelay: `${index * 70}ms` }}
    >
      <Link to={`/product/${product.slug}`} className="block relative">
        {/* Mesh halo backdrop */}
        <div aria-hidden className="absolute inset-0 welljio-mesh opacity-80" />
        <div className="relative aspect-square overflow-hidden bg-welljio-tint">
          <img
            src={product.images[0]}
            alt={product.name}
            className="welljio-card-image w-full h-full object-cover object-center"
            loading="lazy"
          />

          {/* Pills */}
          <div className="absolute top-4 left-4 flex flex-col gap-2 items-start">
            {product.is_bestseller && (
              <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-medium px-3 py-1.5 rounded-full bg-white/90 backdrop-blur text-welljio-ink border border-slate-100">
                <span className="w-1.5 h-1.5 rounded-full bg-welljio-green" />
                Bestseller
              </span>
            )}
            {hasDiscount && (
              <span className="text-[10px] uppercase tracking-[0.22em] font-medium px-3 py-1.5 rounded-full bg-welljio-ink text-white">
                Save {inr(product.compare_at_price - product.price)}
              </span>
            )}
          </div>

          {/* Quick view chip */}
          <span className="absolute bottom-4 right-4 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-medium px-2.5 py-1.5 rounded-full bg-white/90 backdrop-blur text-welljio-ink opacity-0 group-hover:opacity-100 transition-opacity">
            View <ArrowUpRight className="w-3 h-3" strokeWidth={1.8} />
          </span>
        </div>
      </Link>

      {/* Wishlist heart — overlaid, always visible */}
      <button
        type="button"
        data-testid={`wishlist-toggle-${product.slug}`}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(product.id, product.name); }}
        className={`absolute top-4 right-4 z-10 w-9 h-9 rounded-full backdrop-blur border flex items-center justify-center transition-all hover:scale-105 ${
          wished
            ? "bg-welljio-coral border-welljio-coral text-white shadow-[0_8px_20px_-8px_rgba(255,122,89,0.6)]"
            : "bg-white/85 border-slate-100 text-welljio-ink hover:text-welljio-coral hover:border-welljio-coral"
        }`}
        aria-label={wished ? `Remove ${product.name} from wishlist` : `Save ${product.name} to wishlist`}
        aria-pressed={wished}
      >
        <Heart className={`w-4 h-4 ${wished ? "fill-current" : ""}`} strokeWidth={1.6} />
      </button>

      <div className="p-5 sm:p-6 flex-1 flex flex-col gap-3">
        <div className="flex items-start justify-between gap-2">
          <p className="text-[11px] uppercase tracking-[0.22em] text-welljio-subtle">{product.category}</p>
          <div className="flex items-center gap-1 text-xs text-welljio-muted shrink-0">
            <Star className="w-3 h-3 fill-welljio-ink text-welljio-ink" strokeWidth={1} />
            {product.rating.toFixed(1)}
          </div>
        </div>

        <Link to={`/product/${product.slug}`}>
          <h3 className="font-heading text-xl font-semibold text-welljio-ink leading-tight group-hover:text-welljio-accent transition-colors">
            {product.name}
          </h3>
        </Link>

        <p className="text-sm text-welljio-muted line-clamp-2 flex-1">{product.tagline}</p>

        <div className="flex items-end justify-between pt-3 mt-1 border-t border-slate-100">
          <div className="flex items-baseline gap-2">
            <span className="font-heading text-2xl sm:text-[1.65rem] font-semibold tracking-tight text-welljio-ink">{inr(product.price)}</span>
            {hasDiscount && (
              <span className="text-xs text-welljio-subtle line-through">{inr(product.compare_at_price)}</span>
            )}
          </div>
          <button
            data-testid={`add-to-cart-${product.slug}`}
            onClick={() => addItem(product.id, 1)}
            className="group/btn h-10 px-4 rounded-full bg-welljio-ink text-white text-xs font-medium hover:bg-welljio-accent transition-all hover:translate-y-[-1px] inline-flex items-center gap-1.5"
            aria-label={`Add ${product.name} to cart`}
          >
            <ShoppingBag className="w-3.5 h-3.5 transition-transform group-hover/btn:rotate-[-8deg]" strokeWidth={1.6} />
            Add
          </button>
        </div>
      </div>
    </article>
  );
}
